import { Replayer } from 'rrweb'
import { fullSnapshotEvent } from '@rrweb/types'
import { LoggerName, namedLogger } from '../../../../../utils/logger'

const logger = namedLogger(LoggerName.ReplayerProxy)

export type ReplayerCurrentTime = {
  relative: number
  epoch: number
}

export type BufferVideosBeforeReplayerStartsFn = (replayerProxy: ReplayerProxy, replayerCurrentTime: ReplayerCurrentTime) => Promise<void>

export class ReplayerProxy {
  // this one is private so that people don't use replayer.getCurrentTime() because it can be inaccurate and should only be access through the logic below.
  private replayer: Replayer | undefined
  private playerDocument: Document | undefined
  private readonly bufferVideosBeforeReplayerStarts: BufferVideosBeforeReplayerStartsFn

  #currentTime: number = 0

  constructor(replayer: Replayer, playerDocument: Document, bufferVideosBeforeReplayerStarts: BufferVideosBeforeReplayerStartsFn) {
    this.replayer = replayer
    this.playerDocument = playerDocument
    this.bufferVideosBeforeReplayerStarts = bufferVideosBeforeReplayerStarts
  }

  /**
   * @deprecated - this does not provide the time reliably
   * @constructor
   */
  public UNSAFE_getReplayerCurrentTime() {
    return this.replayer?.getCurrentTime()
  }

  set currentTime(value: number) {
    this.#currentTime = value
  }

  get currentTime() {
    const currentTime = this.replayer?.getCurrentTime()
    if (currentTime) {
      return currentTime
    } else {
      logger.debug('falling back to #currentTime', this.#currentTime)
      return this.#currentTime
    }
  }

  /**
   * gets the ReplayerCurrentTime based on the internal state of the proxy or else based on the scrubberPositionInMillis if provided
   * @param scrubberPositionInMillis
   */
  getReplayerCurrentTime(scrubberPositionInMillis?: number): ReplayerCurrentTime {
    if (!this.replayer?.getMetaData().startTime) {
      throw new Error('ReplayerProxy has not yet been initialized')
    }
    return {
      relative: scrubberPositionInMillis ?? this.currentTime,
      epoch: this.replayer?.getMetaData().startTime + (scrubberPositionInMillis ?? this.currentTime)
    }
  }

  async play() {
    logger.debug(
      'ReplayerProxy: play() called - this.currentTime:',
      this.currentTime,
      '- this.replayer?.getCurrentTime():',
      this.replayer?.getCurrentTime()
    )
    if (!this.replayer) {
      throw new Error('replayer is not initialized')
    }

    // ensure that videos are buffered before starting playback, otherwise we may end up in a buffering state immediately
    await this.bufferVideosBeforeReplayerStarts(this, this.getReplayerCurrentTime(this.currentTime))

    this.replayer.play(this.currentTime)
  }

  pause() {
    logger.debug('ReplayerProxy: pause() called')
    if (!this.replayer) {
      throw new Error('replayer is not initialized')
    }
    this.replayer.pause()
  }

  getPlayerDocument(): Document {
    if (!this.playerDocument) {
      throw new Error('playerDocument is not initialized')
    }

    return this.playerDocument
  }

  setReplayer(replayer: Replayer): void {
    this.replayer = replayer
  }

  updatePlayerDocument(event: fullSnapshotEvent): void {
    if (!this.replayer) {
      throw new Error('replayer is not initialized')
    }
    if (event.data.node.rootId) {
      const doc = this.replayer.getMirror().getNode(event.data.node.rootId)
      if (doc instanceof Document) {
        this.playerDocument = doc
      } else {
        logger.error('The root node of the full snapshot is not a Document.')
      }
    }
  }

  getRecordingStartTimeEpoch(): number {
    if (!this.replayer) {
      throw new Error('replayer is not initialized')
    }
    return this.replayer.getMetaData().startTime
  }

  isPlaying(): boolean {
    if (!this.replayer) {
      throw new Error('replayer is not initialized')
    }
    return this.replayer.service.state.value === 'playing'
  }
}
